import { ClientEndpoints, Facilities, EventTriggers } from "@app/shared/src/api/api.gen";
import {
    ClientEndpoint,
    ClientEndpointType,
    DetectionZone,
    DetectionZoneType,
    EmailClientEndpoint,
    EventTrigger,
    Facility,
    FleetDefaults,
    Vector2,
    WebRelayClientEndpoint,
    WebRelayModel,
} from "@app/shared/src/models";
import { EventTriggerType, ImpendingZoneEntryEventTrigger, SpeedingEventTrigger, TargetType } from "@app/shared/src/models";
import { MaxServerIntValue } from "@app/shared/src/helpers/numberHelpers";
import { PassSide, sidePassZoneName, sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";
import {
    buildSidePassZonePoints,
    lanesFromZonePoints,
    lanesToWidthMetres,
    zoneLengthOutOfRange,
    zoneWidthOutOfRange,
} from "@app/shared/src/helpers/sidePassGeometry";
import { ALERT_CATEGORIES, ALERT_CATEGORY_LABELS, AlertCategory } from "./alertCategories";
import { setSpeedingLimit } from "./alertSettings";
import { repairRelayExpressions } from "./notificationSettings";
import { ProgressReporter } from "./progressSteps";

/**
 * Creating the pieces a truck is missing, rather than reporting that it has none.
 *
 * Standardising a fleet is most of the point of this console, and the units that need it most are
 * exactly the ones that were commissioned without a side-pass zone or an email endpoint. A settings
 * screen that can only edit what already exists leaves those trucks permanently unfixable from here
 * and sends a director to the main dashboard to hand-build geometry — which is how fleets drift in
 * the first place.
 *
 * So enabling an alert provisions what it needs, and so does resetting to the fleet default. Both
 * are explicit acts by a director, which is what makes writing facility layout from this screen
 * defensible.
 *
 * Writes are narrow on purpose: each one touches a single zone, trigger or endpoint. The layout
 * endpoint next door takes a facility's whole shape set and soft-deletes whatever is absent from the
 * body, which is right for an editor that owns the canvas and far too broad for provisioning one
 * piece — so this module does not use it.
 */

/** The zone a truck defends: the one impending-entry zone that is not a side pass. */
export function findProtectedZone(facility: Facility): DetectionZone | undefined {
    const zones = (facility.detectionZones ?? []) as DetectionZone[];
    return zones.find((z) => !sideOfZoneName(z.name));
}

export function findSidePassZone(facility: Facility, side: PassSide): DetectionZone | undefined {
    const zones = (facility.detectionZones ?? []) as DetectionZone[];
    return zones.find((z) => sideOfZoneName(z.name) === side);
}

/**
 * Write one zone, leaving every other shape on the site alone.
 *
 * Deliberately not updateLayoutConfiguration: that endpoint takes the facility's whole shape set and
 * soft-deletes anything absent from the body, so a caller touching one zone had to round-trip every
 * screen line, lane line and device it has no interest in. Correct for the layout editor, which owns
 * the canvas; wrong here, where a mistake in a payload we only carry along could retire shapes this
 * console never meant to name.
 *
 * Returns the saved zone so a freshly created one hands back its id.
 */
async function saveZone(facility: Facility, zone: DetectionZone): Promise<DetectionZone | undefined> {
    return Facilities.upsertDetectionZone(facility.id, zone);
}

/** Keep the caller's in-memory facility in step with a zone that was just written. */
function withZone(facility: Facility, zone: DetectionZone): Facility {
    const zones = [...((facility.detectionZones ?? []) as DetectionZone[])];
    const at = zones.findIndex((z) => z.id === zone.id);
    if (at >= 0) zones[at] = zone;
    else zones.push(zone);
    facility.detectionZones = zones;
    return facility;
}

/**
 * Every trigger this console manages runs on the truck itself.
 *
 * The firmware only evaluates triggers marked processOnDevice, and it is the only thing that can
 * fire the horn and beacons the moment a vehicle moves — server-side evaluation happens when the
 * next target batch uploads, seconds after the danger, and cannot reach a relay board on the
 * truck's own network. The generated model defaults the flag to false (the general dashboard's
 * server-analytics case), so every create sets it and every update re-asserts it, healing triggers
 * that were created before this was understood.
 */
export function pinToDevice(trigger: { processOnDevice?: boolean }): void {
    trigger.processOnDevice = true;
}

/**
 * The target types a truck's alerts watch: every vehicle class, and nothing that is not a vehicle.
 *
 * A trigger's TargetFilter is a flag set the device ANDs against each target's type, and a filter of
 * zero matches nothing at all — the wizard used to leave it there, so a freshly onboarded truck had a
 * collision alert that could never fire. "All" on the dashboard is every bit set (MaxServerIntValue),
 * which also covers vehicle classes added later; pedestrians, bikes and wheels are removed because a
 * horn and beacons are for traffic bearing down on the truck, and a wheel is a fragment of one.
 */
export const VEHICLE_TARGET_FILTER = MaxServerIntValue & ~(TargetType.Pedestrian | TargetType.Bike | TargetType.Wheel);

/** A filter the device reads as "no target types": the state every trigger this console created used to be in. */
export function isTargetFilterMissing(trigger: { targetFilter?: number } | undefined): boolean {
    return trigger != null && !trigger.targetFilter;
}

/**
 * Give a trigger the vehicle filter if it has none. Returns whether it changed. A non-zero filter is
 * left alone: someone chose those types on purpose.
 */
export function applyVehicleTargetFilter(trigger: { targetFilter?: number }): boolean {
    if (!isTargetFilterMissing(trigger)) return false;
    trigger.targetFilter = VEHICLE_TARGET_FILTER;
    return true;
}

/**
 * The repairs every save of an existing trigger carries, whether it came from the wizard, a
 * per-truck save, or a reset to the fleet default: a filter the device can match, and relay
 * entries the device will evaluate. Both were silently absent on trucks onboarded by earlier
 * builds, and both made the alert exist on paper while never firing anything.
 */
export function repairTrigger(trigger: EventTrigger): boolean {
    const filtered = applyVehicleTargetFilter(trigger);
    const relays = repairRelayExpressions(trigger);
    return filtered || relays;
}

export type SidePassProvisionResult =
    | { ok: true; facility: Facility; zone: DetectionZone; created: boolean }
    | { ok: false; reason: "no-protected-zone" };

/**
 * Make sure a side-pass zone exists at the requested width, creating or resizing as needed.
 *
 * Refuses when the truck has no protected zone to measure against: a side zone's length comes from
 * that zone, and inventing one would be a fabricated claim about where a driver gets warned. The
 * caller surfaces that as "set the protected zone first" rather than silently doing nothing.
 */
export async function ensureSidePassZone(
    facility: Facility,
    side: PassSide,
    lanes: number,
): Promise<SidePassProvisionResult> {
    const protectedZone = findProtectedZone(facility);
    const points = buildSidePassZonePoints({
        side,
        lanes,
        protectedZonePoints: protectedZone?.points as { x: number; y: number }[] | undefined,
    });
    if (!points) return { ok: false, reason: "no-protected-zone" };

    const asVectors = points.map((p) => new Vector2({ x: p.x, y: p.y } as never));
    const existing = findSidePassZone(facility, side);
    const created = !existing;

    // Resizing keeps the zone's identity, so the trigger watching it stays attached.
    const zone = existing ?? new DetectionZone();
    if (created) {
        zone.name = sidePassZoneName(side);
        zone.facilityId = facility.id;
        // One device per location, so anchoring the zone to it is unambiguous and keeps the zone
        // drawn from the same perspective as the protected zone it was measured from.
        zone.deviceId = facility.devices?.[0]?.id;
        zone.zoneType = protectedZone?.zoneType ?? DetectionZoneType.ParkingArea;
    }
    zone.points = asVectors;

    const saved = await saveZone(facility, zone);
    if (!saved) return { ok: false, reason: "no-protected-zone" };

    return { ok: true, facility: withZone(facility, saved), zone: saved, created };
}

/**
 * Make sure a side-pass trigger exists over `zone`, at the requested threshold and enabled state.
 *
 * A side pass is an impending-entry trigger like the collision alert — what makes it a *pass* is the
 * zone it watches, which is why the zone has to exist first.
 *
 * `reactionTimeSeconds` is the FLEET's driver reaction time, written to MinValue exactly as on the
 * collision trigger. It used to be left empty, which the device does not read as "none": it falls
 * back to 3 seconds (EventTriggerHandler: `MinValue ?? 3`), so every side pass looked five times
 * further ahead than the collision alert beside it on a number nobody chose. A new trigger always
 * gets the fleet's value; an existing one gets it only where the column is still empty — null or
 * <= 0, which is what the server's own floor logic (DriverControlsController.UpdateAlert) treats as
 * "not configured" — because a positive number already there is one somebody set.
 */
export async function ensureSidePassTrigger(options: {
    existing?: EventTrigger;
    zoneId: string;
    facilityId?: string;
    organizationId?: string;
    minSpeedMps: number;
    /** The fleet's driver reaction time. Undefined (defaults not loaded) leaves the column alone. */
    reactionTimeSeconds: number | undefined;
    enabled: boolean;
}): Promise<void> {
    const { existing, zoneId, facilityId, organizationId, minSpeedMps, reactionTimeSeconds, enabled } = options;

    if (existing) {
        existing.detectionZoneId = zoneId;
        existing.additionalValue = minSpeedMps;
        if ((existing.minValue ?? 0) <= 0 && reactionTimeSeconds != null) existing.minValue = reactionTimeSeconds;
        existing.isEnabled = enabled;
        pinToDevice(existing);
        repairTrigger(existing);
        await EventTriggers.update(existing);
        return;
    }

    // EventTriggerConverter dispatches on TriggerType, not $type, and it defaults to 0.
    const trigger = new ImpendingZoneEntryEventTrigger({} as never);
    trigger.triggerType = EventTriggerType.ImpendingZoneEntry;
    trigger.organizationId = organizationId;
    trigger.detectionZoneId = zoneId;
    trigger.additionalValue = minSpeedMps;
    if (reactionTimeSeconds != null) trigger.minValue = reactionTimeSeconds;
    trigger.targetFilter = VEHICLE_TARGET_FILTER;
    trigger.isEnabled = enabled;
    pinToDevice(trigger);
    bindToFacility(trigger as unknown as EventTrigger, facilityId);

    await EventTriggers.update(trigger as unknown as EventTrigger);
}

/**
 * Scope a newly created trigger to one truck's location.
 *
 * Without this the trigger governs the whole organisation: EventTriggerController.GetByFacilityId
 * and EventTriggerProvider.GetEventTriggersUnsecured both read an *empty* facility join as "every
 * facility", so an unbound trigger silently becomes a fleet-wide alert. That is how a side-pass
 * trigger drawn over one truck's zone ended up applying to every truck in the org, and how the cab
 * came to offer an alert the truck already had.
 *
 * Only ever called for triggers being created here. An existing trigger that carries no facility
 * join is org-wide *on purpose* — someone set it that way — and binding it now would silently
 * remove it from every other truck it governs.
 */
function bindToFacility(trigger: EventTrigger, facilityId: string | undefined): void {
    if (!facilityId) return;
    trigger.eventTriggerFacilities = [{ facilityId }] as typeof trigger.eventTriggerFacilities;
}

/**
 * Make sure a speeding trigger exists at the requested threshold and enabled state.
 *
 * Needs no zone: real speeding triggers in the field carry a null detectionZoneId, measuring across
 * whatever the unit sees rather than one polygon.
 *
 * The threshold is written to MaxValue — the device fires outside [MinValue, MaxValue], so that is
 * the limit — and MinValue is cleared. Writing it to MinValue is what made units report speeding on
 * traffic UNDER the limit; see speedingLimitOf.
 */
export async function ensureSpeedingTrigger(options: {
    existing?: EventTrigger;
    facilityId?: string;
    organizationId?: string;
    thresholdMps: number;
    enabled: boolean;
}): Promise<boolean> {
    const { existing, facilityId, organizationId, thresholdMps, enabled } = options;

    if (existing) {
        setSpeedingLimit(existing, thresholdMps);
        existing.isEnabled = enabled;
        pinToDevice(existing);
        repairTrigger(existing);
        await EventTriggers.update(existing);
        return false;
    }

    const trigger = new SpeedingEventTrigger({} as never);
    trigger.triggerType = EventTriggerType.SpeedingEvent;
    trigger.organizationId = organizationId;
    setSpeedingLimit(trigger as unknown as EventTrigger, thresholdMps);
    trigger.targetFilter = VEHICLE_TARGET_FILTER;
    trigger.isEnabled = enabled;
    pinToDevice(trigger);
    bindToFacility(trigger as unknown as EventTrigger, facilityId);

    await EventTriggers.update(trigger as unknown as EventTrigger);
    return true;
}

/**
 * Make sure the impending-collision trigger exists over the truck's protected zone.
 *
 * Refuses when there is no protected zone, because the alert is defined by the area it defends —
 * there is nothing to watch without one, and inventing a zone here would be inventing the truck's
 * footprint.
 */
export async function ensureCollisionTrigger(options: {
    existing?: EventTrigger;
    facility: Facility;
    organizationId?: string;
    minApproachSpeedMps: number;
    reactionTimeSeconds: number;
    sensitivity: number;
}): Promise<{ created: boolean } | { blocked: "no-protected-zone" }> {
    const { existing, facility, organizationId, minApproachSpeedMps, reactionTimeSeconds, sensitivity } = options;

    if (existing) {
        existing.additionalValue = minApproachSpeedMps;
        existing.minValue = reactionTimeSeconds;
        existing.maxValue = sensitivity;
        pinToDevice(existing);
        repairTrigger(existing);
        await EventTriggers.update(existing);
        return { created: false };
    }

    const zone = findProtectedZone(facility);
    if (!zone?.id) return { blocked: "no-protected-zone" };

    const trigger = new ImpendingZoneEntryEventTrigger({} as never);
    trigger.triggerType = EventTriggerType.ImpendingZoneEntry;
    trigger.organizationId = organizationId;
    trigger.detectionZoneId = zone.id;
    trigger.additionalValue = minApproachSpeedMps;
    trigger.minValue = reactionTimeSeconds;
    trigger.maxValue = sensitivity;
    trigger.targetFilter = VEHICLE_TARGET_FILTER;
    // Collision protection has no off switch — see the alerts section.
    trigger.isEnabled = true;
    pinToDevice(trigger);
    // The zone this watches belongs to one truck, so the trigger must too.
    bindToFacility(trigger as unknown as EventTrigger, facility.id);

    await EventTriggers.update(trigger as unknown as EventTrigger);
    return { created: true };
}

/** The name the layout editor gives a truck's protected zone. */
const ATTENUATOR_ZONE_NAME = "Attenuator Zone";

/**
 * Make sure the truck has a protected zone, sized to the fleet's stated TMA footprint.
 *
 * The zone is normally captured when a device is added, so this is the fallback for a unit that
 * arrived without one. Sizing it from the fleet default rather than guessing is what makes that
 * safe: the number came from a director, not from this code.
 *
 * An existing zone is left exactly as it is — a truck whose footprint someone measured is not
 * something to overwrite with a fleet-wide average.
 */
export async function ensureProtectedZone(
    facility: Facility,
    widthMetres: number,
    lengthMetres: number,
): Promise<{ facility: Facility; zone: DetectionZone; created: boolean } | undefined> {
    const existing = findProtectedZone(facility);
    if (existing) return { facility, zone: existing, created: false };

    const zone = newProtectedZone(facility);
    zone.points = protectedZonePoints(widthMetres, lengthMetres);

    const saved = await saveZone(facility, zone);
    if (!saved) return undefined;

    return { facility: withZone(facility, saved), zone: saved, created: true };
}

/** Where the rectangle sits: its lateral centre and the edge nearest the truck. */
type ZoneAnchor = { centreX: number; nearY: number };

/** The layout editor's convention: centred on the unit, starting 1 m behind it. */
const DEFAULT_ANCHOR: ZoneAnchor = { centreX: 0, nearY: -1 };

/**
 * The protected zone's rectangle, matching buildAttenuatorZonePoints in the dashboard's layout
 * editor: x lateral and centred, y running negative away from the truck.
 */
function protectedZonePoints(widthMetres: number, lengthMetres: number, anchor: ZoneAnchor = DEFAULT_ANCHOR): Vector2[] {
    const halfWidth = widthMetres / 2;
    const left = anchor.centreX - halfWidth;
    const right = anchor.centreX + halfWidth;
    const near = anchor.nearY;
    const far = anchor.nearY - lengthMetres;
    return [
        { x: left, y: near },
        { x: right, y: near },
        { x: right, y: far },
        { x: left, y: far },
    ].map((p) => new Vector2({ x: p.x, y: p.y } as never));
}

/** A zone's current anchor, so resizing it changes its extent and nothing else. */
function anchorOf(zone: DetectionZone): ZoneAnchor {
    const points = (zone.points ?? []) as { x?: number; y?: number }[];
    if (points.length < 3) return DEFAULT_ANCHOR;
    const xs = points.map((p) => p.x ?? 0);
    const ys = points.map((p) => p.y ?? 0);
    return { centreX: (Math.min(...xs) + Math.max(...xs)) / 2, nearY: Math.max(...ys) };
}

function newProtectedZone(facility: Facility): DetectionZone {
    const zone = new DetectionZone();
    zone.name = ATTENUATOR_ZONE_NAME;
    zone.facilityId = facility.id;
    zone.deviceId = facility.devices?.[0]?.id;
    zone.zoneType = DetectionZoneType.ParkingArea;
    return zone;
}

/** Width and length of a zone's footprint in metres, or undefined when its shape can't be read. */
export function protectedZoneSize(zone: DetectionZone | undefined): { width: number; length: number } | undefined {
    const points = (zone?.points ?? []) as { x?: number; y?: number }[];
    if (points.length < 3) return undefined;
    const xs = points.map((p) => p.x ?? 0);
    const ys = points.map((p) => p.y ?? 0);
    const width = Math.max(...xs) - Math.min(...xs);
    const length = Math.max(...ys) - Math.min(...ys);
    return width > 0 && length > 0 ? { width, length } : undefined;
}

export type ProtectedZoneResizeResult =
    | { ok: true; facility: Facility; zone: DetectionZone; created: boolean; refitted: PassSide[] }
    | { ok: false; reason: "out-of-range" | "save-failed" };

/**
 * Set the size of this truck's protected zone — the one place a per-unit TMA footprint is edited
 * from this console.
 *
 * The fleet's stated footprint is only ever a starting size (see ensureProtectedZone): attenuators
 * differ, and the cushion on this truck is whatever someone measured. So this writes exactly the
 * size given, keeps the zone's identity so the collision trigger stays attached, and keeps its
 * position — only the extent changes. A truck with no zone yet gets one at the size given.
 *
 * Side-pass zones are measured off the protected zone (buildSidePassZonePoints): they abut its
 * edges and run its length. Left as they were, a widened cushion would overlap them and a
 * lengthened one would outrun them, so each side that exists is re-fitted at the lane count it
 * already covers.
 */
export async function setProtectedZoneSize(
    facility: Facility,
    widthMetres: number,
    lengthMetres: number,
): Promise<ProtectedZoneResizeResult> {
    // Guarded here as well as on screen: what this writes is what the truck runs, and the layout
    // editor will not draw a zone outside these bounds.
    if (zoneWidthOutOfRange(widthMetres) || zoneLengthOutOfRange(lengthMetres)) return { ok: false, reason: "out-of-range" };

    const existing = findProtectedZone(facility);
    const created = !existing;
    const zone = existing ?? newProtectedZone(facility);
    zone.points = protectedZonePoints(widthMetres, lengthMetres, existing ? anchorOf(existing) : DEFAULT_ANCHOR);

    const saved = await saveZone(facility, zone);
    if (!saved) return { ok: false, reason: "save-failed" };
    let current = withZone(facility, saved);

    const refitted: PassSide[] = [];
    for (const side of ["left", "right"] as PassSide[]) {
        const sideZone = findSidePassZone(current, side);
        if (!sideZone) continue;
        const lanes = lanesFromZonePoints(sideZone.points as { x: number; y: number }[] | undefined);
        if (lanes === undefined) continue;
        const result = await ensureSidePassZone(current, side, lanes);
        if (!result.ok) continue;
        current = result.facility;
        refitted.push(side);
    }

    return { ok: true, facility: current, zone: saved, created, refitted };
}

/**
 * Make sure the site has an email endpoint carrying the fleet's recipients.
 *
 * Returns undefined when the fleet has stated no addresses — an endpoint that reaches nobody looks
 * configured while notifying no one, which is worse than having none at all. The caller says so
 * rather than creating a decoy.
 */
export async function ensureEmailEndpoint(
    facility: Facility,
    organizationId: string | undefined,
    addresses: string | undefined,
    existing: ClientEndpoint[],
): Promise<ClientEndpoint | undefined> {
    const found = existing.find((e) => ((e as { $type?: string }).$type ?? "").includes("Email"));
    if (found) return found;

    if (!addresses?.trim()) return undefined;

    const endpoint = new EmailClientEndpoint({} as never);
    // The server's binder dispatches on this enum, not the $type tag — and the generated class
    // initialises it to the BASE default (Json), whose model then rejects the payload for having
    // no EndpointUrl. Without this line the endpoint cannot be created at all.
    endpoint.endpointType = ClientEndpointType.Email;
    endpoint.facilityId = facility.id;
    endpoint.organizationId = organizationId;
    endpoint.endpointName = "Fleet notifications";
    endpoint.emailAddress = addresses.trim();

    await ClientEndpoints.update(endpoint as unknown as ClientEndpoint);

    const reloaded = (await ClientEndpoints.getAll(organizationId, facility.id)) ?? [];
    return reloaded.find((e) => ((e as { $type?: string }).$type ?? "").includes("Email"));
}

/**
 * Make sure the site has a relay board endpoint at the fleet's address.
 *
 * The standard build ships every truck's relay on the same address, which is why this is a fleet
 * default rather than something asked per unit. The model is left to the device: trucks carry
 * every generation of board (single and quad, original and 400 series) and a director adding one
 * has no way to know which, so the endpoint is provisioned as Auto and the firmware reads the
 * board's state.xml to find out. Provisioning Single, as this used to, silently muted every quad
 * and every 400-series board. Auto is safe to store for EVERY unit, whatever its firmware: the
 * server hands a unit that cannot detect (below FirmwareHelpers.WEB_RELAY_AUTO_DETECT_FIRMWARE_VERSION,
 * which includes the 2.0.11.3 build whose detection misjudged the fleet's quads) the standard
 * build's quad in its device-bound payloads instead (DeviceEndpointCompatibility), and the stored
 * Auto takes effect by itself once the unit is upgraded.
 */
export async function ensureRelayEndpoint(
    facility: Facility,
    organizationId: string | undefined,
    ipAddress: string | undefined,
    existing: ClientEndpoint[],
): Promise<ClientEndpoint | undefined> {
    const found = existing.find((e) => ((e as { $type?: string }).$type ?? "").includes("WebRelay"));
    if (found) return found;

    if (!ipAddress?.trim()) return undefined;

    const endpoint = new WebRelayClientEndpoint({} as never);
    // Same story as the email endpoint above: the binder reads this enum, and the generated
    // default is Json — which 400s on a missing EndpointUrl instead of creating a relay board.
    endpoint.endpointType = ClientEndpointType.WebRelay;
    endpoint.facilityId = facility.id;
    endpoint.organizationId = organizationId;
    endpoint.endpointName = "Relay board";
    endpoint.ipAddress = ipAddress.trim();
    endpoint.model = WebRelayModel.Auto;

    await ClientEndpoints.update(endpoint as unknown as ClientEndpoint);

    const reloaded = (await ClientEndpoints.getAll(organizationId, facility.id)) ?? [];
    return reloaded.find((e) => ((e as { $type?: string }).$type ?? "").includes("WebRelay"));
}

export type MissingAlertProvisionResult = {
    /** The caller's facility, carrying any zones created along the way. */
    facility: Facility;
    /** Categories now backed by a freshly created trigger, in need of wiring. */
    created: AlertCategory[];
    /** What was declined or failed, in words a director can act on. */
    notes: string[];
};

/**
 * Whether the fleet actually runs this alert — the gate provisioning applies before creating one.
 *
 * Exported because the screens that *offer* the repair must ask the same question the repair
 * *answers*, and they previously did not. "Does the fleet notify on this alert?" (an email or relay
 * default) is not the same as "does the fleet run this alert?" (its enabled flag), and the two are
 * edited on different screens — the enable flags under Alert defaults, the relay and email choices
 * under Notifications — with nothing keeping them consistent. A fleet that switched a side pass off
 * and left its relay selection at the stock Lights therefore had every truck missing that trigger
 * offering a reset that declined to create it, leaving a "Not set up" banner no button could clear.
 *
 * So this predicate is the single source of truth: provisionMissingAlerts gates on it, and the
 * device page filters what it advertises through it. They can disagree only if someone edits one
 * caller and not the other, which is what this function exists to prevent.
 *
 * Collision is unconditionally true — it has no off switch here, on the device, or in the cab.
 */
export function fleetRunsAlert(defaults: FleetDefaults, category: AlertCategory): boolean {
    switch (category) {
        case "collision":
            return true;
        case "speeding":
            return defaults.speedingEnabled;
        case "left":
            return defaults.leftPassEnabled;
        case "right":
            return defaults.rightPassEnabled;
    }
}

/**
 * Create the triggers a truck should have but does not, at the fleet's stated values.
 *
 * This is the repair path for a unit that was never onboarded properly: the wizard builds all of
 * this for a new truck, but a truck adopted before the wizard existed — or one whose provisioning
 * partially failed — has no trigger for the fleet's notification rules to attach to, and the fleet
 * page deliberately refuses to invent one (see planAdditionalAlertPush). So "reset to the fleet
 * default" on the device page creates what is missing, the one explicit act that makes writing
 * zones and triggers from a settings screen defensible.
 *
 * Everything comes from the fleet defaults record rather than a guess: the protected zone gets the
 * stated TMA footprint, thresholds get the stated values, and an alert the fleet has switched off
 * is declined rather than fabricated — a zone drawn for an alert nobody asked to run is clutter at
 * best and a fabricated claim about the truck at worst. Collision is the exception with no gate,
 * because it has no off switch anywhere in the system.
 *
 * Categories are processed in the fixed ALERT_CATEGORIES order so the collision trigger's protected
 * zone exists before the side-pass zones that are measured off it.
 */
/** The progress step each alert reports under — exported so a caller's plan and the work agree. */
export function provisionStepLabel(category: AlertCategory): string {
    return `Setting up ${ALERT_CATEGORY_LABELS[category].toLowerCase()}`;
}

/** The step a missing protected zone reports under, for callers planning a progress bar ahead. */
export const PROTECTED_ZONE_STEP = { key: "zone", label: "Drawing the protected zone" } as const;

export async function provisionMissingAlerts(options: {
    facility: Facility;
    organizationId: string | undefined;
    defaults: FleetDefaults;
    /** Which alerts to create — the caller decides what is missing and wanted. */
    categories: AlertCategory[];
    /**
     * Told about each step as it starts and settles, so a caller can draw a progress bar. Steps
     * report under the category as their key, plus PROTECTED_ZONE_STEP when a zone has to be drawn
     * — the same keys and labels provisionStepLabel / PROTECTED_ZONE_STEP let a caller plan with.
     */
    onProgress?: ProgressReporter;
}): Promise<MissingAlertProvisionResult> {
    const { organizationId, defaults, categories } = options;
    const report = options.onProgress ?? (() => undefined);
    let facility = options.facility;
    const created: AlertCategory[] = [];
    const notes: string[] = [];

    // Split before doing any work. The gate is the caller's to apply — the device page filters on
    // fleetRunsAlert so it never advertises a repair this would decline — but it is re-applied here
    // as a backstop, and applied *first* so that a call consisting only of switched-off alerts
    // writes nothing at all, rather than leaving behind a protected zone nobody asked for.
    const wanted = ALERT_CATEGORIES.filter((c) => categories.includes(c));
    const runnable = wanted.filter((c) => fleetRunsAlert(defaults, c));

    for (const category of wanted.filter((c) => !fleetRunsAlert(defaults, c))) {
        const note = `${ALERT_CATEGORY_LABELS[category]} is switched off in the fleet defaults, so no trigger was created for it.`;
        notes.push(note);
        report({ key: category, label: provisionStepLabel(category), state: "skipped", note });
    }

    if (runnable.length === 0) return { facility, created, notes };

    // Every category except speeding is defined by a zone, and the side zones are sized off the
    // protected one — so it is ensured first whenever any zone-backed alert is being created.
    // Reported as a step only when there is one to draw: a site that already has its zone gets no
    // step, which is what a caller that planned from findProtectedZone expects to see.
    if (runnable.some((c) => c !== "speeding")) {
        const drawing = !findProtectedZone(facility);
        if (drawing) report({ ...PROTECTED_ZONE_STEP, state: "active" });
        const zoned = await ensureProtectedZone(facility, defaults.tmaWidthMetres, defaults.tmaLengthMetres);
        if (zoned) facility = zoned.facility;
        if (drawing) {
            report(
                zoned
                    ? { ...PROTECTED_ZONE_STEP, state: "done" }
                    : { ...PROTECTED_ZONE_STEP, state: "failed", note: "The protected zone could not be created." },
            );
        }
    }

    for (const category of runnable) {
        const label = ALERT_CATEGORY_LABELS[category];
        const step = provisionStepLabel(category);
        report({ key: category, label: step, state: "active" });
        try {
            if (category === "collision") {
                const result = await ensureCollisionTrigger({
                    facility,
                    organizationId,
                    minApproachSpeedMps: defaults.minimumApproachSpeedMps,
                    reactionTimeSeconds: defaults.driverReactionTimeSeconds,
                    sensitivity: defaults.sensitivity,
                });
                if ("blocked" in result) {
                    const note = "Impending collision could not be set up because the protected zone could not be created.";
                    notes.push(note);
                    report({ key: category, label: step, state: "failed", note });
                } else {
                    created.push(category);
                    report({ key: category, label: step, state: "done" });
                }
            } else if (category === "speeding") {
                await ensureSpeedingTrigger({
                    facilityId: facility.id,
                    organizationId,
                    thresholdMps: defaults.speedingThresholdMps,
                    enabled: true,
                });
                created.push(category);
                report({ key: category, label: step, state: "done" });
            } else {
                const lanes = category === "left" ? defaults.leftPassLanes : defaults.rightPassLanes;
                const zoned = await ensureSidePassZone(facility, category, lanes);
                if (!zoned.ok) {
                    const note = `The ${label.toLowerCase()} needs the protected zone, which could not be created.`;
                    notes.push(note);
                    report({ key: category, label: step, state: "failed", note });
                    continue;
                }
                facility = zoned.facility;
                await ensureSidePassTrigger({
                    zoneId: zoned.zone.id!,
                    facilityId: facility.id,
                    organizationId,
                    minSpeedMps: defaults.sidePassMinSpeedMps,
                    reactionTimeSeconds: defaults.driverReactionTimeSeconds,
                    enabled: true,
                });
                created.push(category);
                report({ key: category, label: step, state: "done" });
            }
        } catch {
            const note = `The ${label} trigger could not be created — try again from Alerts & Relays.`;
            notes.push(note);
            report({ key: category, label: step, state: "failed", note });
        }
    }

    return { facility, created, notes };
}

/** Lanes a width represents — re-exported so callers do not reach past this module for it. */
export { lanesToWidthMetres };
